import { useEffect, useRef, useState } from "react";
import { Bell, CheckCircle2, Coins, ShieldCheck, CalendarClock, XCircle, Banknote, MessageSquareText } from "lucide-react";
import api, { fmtDate } from "@/lib/api";

const ICON = { approved: CheckCircle2, credit: Coins, kyc: ShieldCheck, maturity: CalendarClock, rejected: XCircle, payout: Banknote, support: MessageSquareText };
const COLOR = { approved: "text-emerald-400", credit: "text-[#D4AF37]", kyc: "text-sky-400", maturity: "text-amber-400", rejected: "text-red-400", payout: "text-emerald-400", support: "text-violet-300" };

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post("/notifications/read-all").catch(() => {});
      setData((d) => ({ items: d.items.map((i) => ({ ...i, read: true })), unread: 0 }));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={toggle} aria-label="Notifications" className="relative text-white/70 hover:text-[#D4AF37] transition-colors p-1">
        <Bell size={18} />
        {data.unread > 0 && (
          <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#D4AF37] text-black text-[9px] font-bold flex items-center justify-center">{data.unread > 9 ? "9+" : data.unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-3 w-[360px] max-w-[92vw] bg-[#0A0A0A] border border-[#D4AF37]/30 shadow-2xl z-[70]">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Activity</p>
            <span className="text-[10px] text-white/35">{data.items.length} updates</span>
          </div>
          <ul className="max-h-[420px] overflow-y-auto divide-y divide-white/5" data-testid="notification-list">
            {data.items.length === 0 && <li className="px-5 py-8 text-sm text-white/40" data-testid="notification-empty">No activity yet. Approvals, credits, KYC updates and payouts will appear here.</li>}
            {data.items.map((n) => {
              const Icon = ICON[n.type] || Bell;
              return (
                <li key={n.id} className={`px-5 py-3 flex gap-3 ${n.read ? "" : "bg-[#D4AF37]/5"}`} data-testid={`notification-item-${n.type}`}>
                  <Icon size={15} className={`mt-0.5 shrink-0 ${COLOR[n.type] || "text-white/50"}`} />
                  <div className="min-w-0">
                    <p className="text-sm text-white leading-tight">{n.title}</p>
                    <p className="text-xs text-white/50 mt-1 leading-relaxed">{n.body}</p>
                    <p className="text-[10px] text-white/30 mt-1">{fmtDate(n.created_at)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
